import { api } from '@utils/index'
import { setPantoniReference } from '@utils/editor-bozze'
import { EditorColoreApiType, EditorImageType, EditorTextType } from '@utils/types'
import { PANTONI } from 'editor-bozze/utils/endpoints'
import { useRouter } from 'next/router'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'

export interface EditorColorLimits {
  allowedColors: EditorColoreApiType[]
  lockColorChange: EditorColoreApiType | null
}

interface EditorContextType {
  canvases: Record<string, any>
  updateCanvasData: (
    id: number,
    data: any,
    preview?: string,
    isDirty?: boolean,
    previewByColorId?: Record<string, string>
  ) => void
  removeCanvasData: (id: number | string) => void
  registerActions: (id: string, actions: CanvasActions) => void
  unregisterActions: (id: string) => void
  getActions: (canvasId: string) => CanvasActions | null
  activeCanvasId: string | null
  setActiveCanvasId: (id: string | null) => void
  activeCanvas: any | null
  pantoni: EditorColoreApiType[]
  pantoniLoading: boolean
  pantoniError: any
  reloadPantoni: () => Promise<void>
  colorLimits: EditorColorLimits
  setColorLimits: (limits: EditorColorLimits) => void
}

export interface CanvasActions {
  handleObjectAction: (id: string, action: string, payload: any) => void
  handleColorChange: (id: string | number, o: string, n: string) => void
  handleMonochromaticChange: (id: string | number, checked: boolean) => void
  handleNoBackgroundChange: (id: string | number, checked: boolean) => void
  loadText: (text: EditorTextType) => void
  handleDownloadPreview: (filename: string) => void
  getPreviewImage: () => Promise<string | null>
  loadImage: (image: EditorImageType, meta?: any) => void
  saveCanvasJson: () => any
  saveTransferCanvasJson: () => any
  replaceCanvasJson: (canvasJson: any) => Promise<void>
  resetDirty: (newObjectControls: any[]) => void
}

const EditorContext = React.createContext<EditorContextType | undefined>(undefined)

export const EditorContextProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter()
  const [canvases, setCanvases] = useState<Record<string, any>>({})
  const [activeCanvasId, setActiveCanvasId] = useState<string | null>(null)
  const [pantoni, setPantoni] = useState<EditorColoreApiType[]>([])
  const [pantoniLoading, setPantoniLoading] = useState(false)
  const [pantoniError, setPantoniError] = useState<any>(null)
  const [colorLimits, setColorLimits] = useState<EditorColorLimits>({
    allowedColors: [],
    lockColorChange: null,
  })
  // A Ref is better for storing functions because it doesn't trigger re-renders
  const canvasActionsRef = useRef<Record<string, any>>({})

  const updateCanvasData = useCallback(
    (
      id: number,
      data: any,
      preview: string,
      isDirty: boolean,
      previewByColorId: Record<string, string> = {}
    ) => {
      setCanvases((prev) => ({
        ...prev,
        // [id]: [...(prev[id] || []), ...data],
        [id]: {
          ...prev[id],
          data: data,
          preview: preview,
          previewByColorId: previewByColorId,
          isDirty: isDirty,
        },
      }))
    },
    []
  )

  const removeCanvasData = useCallback((id: number | string) => {
    setCanvases((prev) => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
    delete canvasActionsRef.current[id]
  }, [])

  const registerActions = useCallback((id: string, actions: any) => {
    canvasActionsRef.current[id] = actions
  }, [])

  const unregisterActions = useCallback((id: string) => {
    delete canvasActionsRef.current[id]
  }, [])

  const getActions = (canvasId: string): CanvasActions | null => {
    return canvasActionsRef.current[canvasId] || null
  }

  const reloadPantoni = useCallback(async () => {
    setPantoniLoading(true)
    setPantoniError(null)

    try {
      const response = await api.get(PANTONI, {
        params: { lang: router.locale },
      })

      const data = response.data
      setPantoni(data)
      setPantoniReference(data)
    } catch (error) {
      setPantoniError(error)
      setPantoni([])
      setPantoniReference([])
    } finally {
      setPantoniLoading(false)
    }
  }, [router.locale])

  useEffect(() => {
    reloadPantoni()
  }, [reloadPantoni])

  const getActiveCanvas = () => {
    if (!activeCanvasId) return null
    return {
      id: activeCanvasId,
      data: canvases[activeCanvasId]?.data,
      preview: canvases[activeCanvasId]?.preview,
      previewByColorId: canvases[activeCanvasId]?.previewByColorId,
      isDirty: canvases[activeCanvasId]?.isDirty,
      selectedObject: canvases[activeCanvasId]?.data?.find((oc) => oc.isSelected),
      actions: canvasActionsRef.current[activeCanvasId],
    }
  }

  return (
    <EditorContext.Provider
      value={{
        canvases,
        updateCanvasData,
        removeCanvasData,
        registerActions,
        unregisterActions,
        getActions,
        activeCanvasId,
        setActiveCanvasId,
        activeCanvas: getActiveCanvas(),
        pantoni,
        pantoniLoading,
        pantoniError,
        reloadPantoni,
        colorLimits,
        setColorLimits,
      }}
    >
      {children}
    </EditorContext.Provider>
  )
}

export const useEditorContext = () => useContext(EditorContext)
